/**
 * Resolve the merchant-facing agent summary for Task Library run history.
 * Treats the legacy '(completed)' placeholder as empty, never shows raw JSON /
 * stack traces / code dumps, and drops sentences that name internal machinery.
 */
import { looksLikeProgressNote } from './scheduledTaskSummaryHeuristics'

export { looksLikeProgressNote } from './scheduledTaskSummaryHeuristics'

const INTERNAL_DETAIL_PATTERNS: RegExp[] = [
  /\b(?:pending_|live_in_)?solr\b/i,
  /\bre-?index(?:ing|ed|es)?\b/i,
  /\bbackfill(?:s|ing|ed)?\b/i,
  /\bdeploy(?:s|ing|ed|ment|ments)?\b/i,
  /\bGraphQL\b/i,
  /\bpayouts?\s+api\b/i,
  /\b[\w./-]+\.(?:ts|tsx|js|jsx|py|sql|sh|rb|go)\b/i,
  /(?:^|\s)\/(?:api|v\d)\/[\w/-]+/i,
]

const SENTENCE_SPLIT = /(?<=[.!?])\s+/

function containsInternalDetail(s: string): boolean {
  return INTERNAL_DETAIL_PATTERNS.some((re) => re.test(s))
}

function redactInternalDetails(text: string): string {
  if (!text) return text
  const kept: string[] = []
  for (const line of text.split('\n')) {
    if (!containsInternalDetail(line)) {
      kept.push(line)
      continue
    }
    const rebuilt = line
      .split(SENTENCE_SPLIT)
      .filter((s) => !containsInternalDetail(s))
      .join(' ')
      .trim()
    if (!rebuilt || /^(?:[-*]|\d+[.)]|#+)?\s*$/.test(rebuilt)) continue
    kept.push(rebuilt)
  }
  return kept.join('\n')
}

function looksLikeInternalDump(text: string): boolean {
  const t = text.trim()
  if (!t) return true
  if (t.startsWith('{') || t.startsWith('[')) return true
  if (/^Error executing\b/i.test(t)) return true
  if (/\b(?:TypeError|ReferenceError|SyntaxError|RangeError)\b/.test(t)) return true
  if (/\bat\s+\S+\s+\([^)]+\.(?:ts|tsx|js|jsx):\d+/i.test(t)) return true
  if (/```(?:ts|tsx|js|jsx|python|py|sql|bash|sh|go)\b/i.test(t)) return true
  return false
}

export function sanitizeScheduledTaskDisplaySummary(text: string | null | undefined): string {
  if (typeof text !== 'string') return ''
  const trimmed = text.trim()
  if (!trimmed || trimmed === '(completed)') return ''
  if (looksLikeInternalDump(trimmed)) return ''
  if (looksLikeProgressNote(trimmed)) return ''
  return redactInternalDetails(trimmed).trim()
}

export function resolveScheduledTaskDisplaySummary(responseData: any): string {
  if (!responseData || typeof responseData !== 'object') return ''

  const fromFinal = sanitizeScheduledTaskDisplaySummary(responseData.final_response)
  if (fromFinal) return fromFinal

  const output = responseData.output
  if (typeof output === 'string') {
    return sanitizeScheduledTaskDisplaySummary(output)
  }

  return ''
}
